<template>
  <section class="page-shell judicial-metadata-page">
    <div class="page-heading">
      <div>
        <h1>Metadados Judiciais CNJ</h1>
        <p>Consulte capas processuais e movimentações públicas do DataJud a partir da numeração única CNJ.</p>
      </div>
    </div>

    <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
    <v-alert v-if="success" type="success" dense text>{{ success }}</v-alert>

    <section class="data-panel judicial-query-panel">
      <div>
        <div class="panel-kicker">Consulta DataJud</div>
        <h2>Número do processo</h2>
        <p>Use a numeração única CNJ. O tribunal é inferido automaticamente pelos dígitos do processo.</p>
      </div>
      <div class="judicial-query-workspace">
        <v-form class="judicial-query-form" @submit.prevent="query">
          <v-text-field
            v-model="processNumber"
            label="Processo"
            placeholder="0000000-00.0000.0.00.0000"
            outlined
            dense
            hide-details
            maxlength="25"
            @input="formatProcessNumber"
          />
          <v-btn color="primary" height="40" type="submit" :loading="loading">
            <v-icon left small>mdi-database-search-outline</v-icon>
            Consultar
          </v-btn>
        </v-form>
      </div>
    </section>

    <section v-if="result" class="data-panel judicial-result-panel">
      <div class="judicial-result-header">
        <div>
          <span>Processo</span>
          <h2>{{ result.query.formatted_process_number }}</h2>
          <p>{{ result.query.tribunal_name }} · {{ result.total }} resultado(s) público(s)</p>
        </div>
        <div class="judicial-result-actions">
          <v-btn color="primary" @click="useAsDocumentContext">
            <v-icon left small>mdi-file-document-edit-outline</v-icon>
            Usar contexto
          </v-btn>
          <v-btn outlined color="primary" @click="copyResult">
            <v-icon left small>mdi-content-copy</v-icon>
            Copiar
          </v-btn>
          <v-btn text @click="reset">
            <v-icon left small>mdi-refresh</v-icon>
            Nova consulta
          </v-btn>
        </div>
      </div>

      <v-alert v-if="!result.processes.length" type="info" dense text>
        Nenhum processo público foi localizado no DataJud para esta numeração.
      </v-alert>

      <section v-if="contextBlocks.length" class="judicial-context-builder">
        <div>
          <strong>Contexto para documento</strong>
          <span>{{ selectedContextText.length }} caracteres selecionados</span>
        </div>
        <div class="context-block-grid">
          <label v-for="block in contextBlocks" :key="block.id" class="context-block-option">
            <input
              type="checkbox"
              :checked="selectedContextBlockIds.includes(block.id)"
              @change="toggleContextBlock(block.id)"
            >
            <span>
              <strong>{{ block.label }}</strong>
              <small>{{ block.size }} caracteres</small>
            </span>
          </label>
        </div>
      </section>

      <article v-for="process in result.processes" :key="process.id || process.process_number" class="judicial-process-card">
        <div v-if="process.summary" class="judicial-summary-strip">
          <div>
            <span>Resumo</span>
            <strong>{{ process.summary.headline }}</strong>
          </div>
          <div v-if="process.summary.latest_movement">
            <span>Último movimento</span>
            <strong>{{ formatDateTime(process.summary.latest_movement.date) }} · {{ process.summary.latest_movement.name }}</strong>
          </div>
          <v-alert v-if="process.summary.context_warnings && process.summary.context_warnings.length" type="warning" dense text>
            {{ process.summary.context_warnings.join(' ') }}
          </v-alert>
        </div>

        <div class="judicial-section-title">
          <v-icon>mdi-file-document-outline</v-icon>
          <strong>Identificação do processo</strong>
        </div>

        <div class="metadata-grid">
          <div v-for="item in identificationItems(process)" :key="item.label">
            <span>{{ item.label }}</span>
            <strong>{{ item.value }}</strong>
          </div>
        </div>

        <div class="judicial-section-title">
          <v-icon>mdi-account-multiple-outline</v-icon>
          <strong>Partes do processo</strong>
          <span v-if="process.parties.length">{{ process.parties.length }} parte(s)</span>
        </div>
        <div v-if="process.parties.length" class="party-list">
          <article v-for="party in process.parties" :key="`${party.pole}-${party.role}-${party.name}`" class="party-item">
            <strong>{{ party.name }}</strong>
            <span>{{ partyDetail(party) }}</span>
          </article>
        </div>
        <p v-else class="empty-text">Partes não informadas no retorno público do DataJud.</p>

        <div class="judicial-section-title">
          <v-icon>mdi-database-cog-outline</v-icon>
          <strong>Dados técnicos DataJud</strong>
        </div>
        <div class="metadata-grid metadata-grid--technical">
          <div v-for="item in technicalItems(process)" :key="item.label">
            <span>{{ item.label }}</span>
            <strong>{{ item.value }}</strong>
          </div>
        </div>

        <div class="judicial-section-title">
          <v-icon>mdi-tag-multiple-outline</v-icon>
          <strong>Assuntos do processo</strong>
        </div>
        <div class="subject-list">
          <v-chip v-for="subject in process.subjects" :key="subject.code || subject.name" small outlined>
            {{ subject.name || subject.code }}
          </v-chip>
          <span v-if="!process.subjects.length" class="empty-text">Nenhum assunto informado.</span>
        </div>

        <div class="judicial-section-title">
          <v-icon>mdi-timeline-text-outline</v-icon>
          <strong>Movimentos processuais</strong>
          <span>{{ movementCountLabel(process) }}</span>
        </div>

        <v-alert v-if="process.movements_truncated" type="info" dense text>
          O DataJud retornou {{ process.movements_total }} movimento(s). Exibimos os {{ process.movements.length }} mais recentes para manter a leitura rápida.
        </v-alert>

        <div class="movement-list">
          <article v-for="movement in visibleMovements(process)" :key="`${movement.code}-${movement.date}-${movement.name}`" class="movement-item">
            <time>{{ formatDateTime(movement.date) }}</time>
            <strong>{{ movement.name || movement.code || 'Movimento sem descrição' }}</strong>
            <p v-if="movement.judging_body && movement.judging_body.name">{{ movement.judging_body.name }}</p>
            <div v-if="movement.complements.length" class="movement-complements">
              <span v-for="item in movement.complements" :key="`${item.code}-${item.value}-${item.name}`">
                {{ item.name || item.description || item.value }}
              </span>
            </div>
          </article>
          <p v-if="!process.movements.length" class="empty-text">Nenhum movimento informado.</p>
        </div>

        <v-btn
          v-if="process.movements.length > movementLimit"
          text
          color="primary"
          class="mt-4"
          @click="toggleMovements(process.id || process.process_number)"
        >
          {{ expandedMovementKeys.includes(process.id || process.process_number) ? 'Mostrar menos' : `Ver todos os ${process.movements.length} movimentos` }}
        </v-btn>
      </article>

      <details class="raw-metadata-details">
        <summary>Ver JSON normalizado</summary>
        <pre>{{ normalizedJson }}</pre>
      </details>
    </section>

  </section>
</template>

<script>
import api from '../../plugins/axios'

const documentContextDraftKey = 'complyops:document-generator-context'
const contextMaxLength = 18000

export default {
  name: 'JudicialMetadataPage',
  data () {
    return {
      processNumber: '',
      loading: false,
      error: '',
      success: '',
      result: null,
      movementLimit: 12,
      expandedMovementKeys: [],
      selectedContextBlockIds: []
    }
  },
  computed: {
    normalizedJson () {
      return JSON.stringify(this.result, null, 2)
    },
    contextBlocks () {
      return this.result && Array.isArray(this.result.context_blocks) ? this.result.context_blocks : []
    },
    selectedContextText () {
      return this.selectedBlocksText(this.contextBlocks, this.selectedContextBlockIds)
    }
  },
  methods: {
    async query () {
      this.loading = true
      this.error = ''
      this.success = ''

      try {
        const { data } = await api.post('/judicial-metadata/query', {
          process_number: this.processNumber
        })
        this.result = this.normalizeClientResult(data)
        this.expandedMovementKeys = []
        this.selectedContextBlockIds = this.defaultContextBlockIds(this.result.context_blocks)
      } catch (error) {
        this.error = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Não foi possível consultar os metadados judiciais.'
      } finally {
        this.loading = false
      }
    },
    reset () {
      this.result = null
      this.error = ''
      this.success = ''
      this.expandedMovementKeys = []
      this.selectedContextBlockIds = []
    },
    async copyResult () {
      if (!this.result) return

      const text = this.result.normalized_text || this.normalizedJson

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        const textarea = document.createElement('textarea')
        textarea.value = text
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }

      this.success = 'Metadados copiados para a área de transferência.'
    },
    useAsDocumentContext () {
      if (!this.result) return

      const process = this.result.processes && this.result.processes[0] ? this.result.processes[0] : null
      const formattedNumber = this.result.query && this.result.query.formatted_process_number
        ? this.result.query.formatted_process_number
        : this.processNumber
      const tribunalName = this.result.query && this.result.query.tribunal_name
        ? this.result.query.tribunal_name
        : ''
      const normalizedText = this.clippedContextText(this.selectedContextText || this.result.normalized_text || this.normalizedJson)

      localStorage.setItem(documentContextDraftKey, JSON.stringify({
        source: 'datajud',
        source_label: 'Metadados Judiciais CNJ',
        document_type: 'Petição',
        purpose: `Criar documento com base nos metadados públicos do processo ${formattedNumber}.`,
        client_name: '',
        matter_reference: formattedNumber,
        destination: 'Protocolar em processo',
        tone: 'Técnico e objetivo',
        facts: normalizedText,
        instructions: [
          'Use os metadados DataJud apenas como contexto factual inicial.',
          'Não invente partes, pedidos, fundamentos, datas, movimentações ou decisões que não estejam no contexto.',
          'Sinalize informações ausentes que precisam ser complementadas antes do protocolo.',
          tribunalName ? `Tribunal inferido na consulta: ${tribunalName}.` : ''
        ].filter(Boolean).join('\n'),
        metadata: {
          process_number: formattedNumber,
          tribunal: process && process.tribunal ? process.tribunal : null,
          class_name: process && process.class ? process.class.name : null,
          subjects: process && Array.isArray(process.subjects) ? process.subjects.map(item => item.name || item.code).filter(Boolean) : [],
          context_blocks: this.selectedContextBlockIds
        },
        created_at: new Date().toISOString()
      }))
      this.$router.push({ name: 'document-generator', query: { context: 'datajud' } })
    },
    defaultContextBlockIds (blocks) {
      return (Array.isArray(blocks) ? blocks : [])
        .filter(block => block.enabled !== false)
        .map(block => block.id)
    },
    selectedBlocksText (blocks, selectedIds) {
      return (Array.isArray(blocks) ? blocks : [])
        .filter(block => selectedIds.includes(block.id))
        .map(block => block.text)
        .filter(Boolean)
        .join('\n\n')
    },
    toggleContextBlock (blockId) {
      this.selectedContextBlockIds = this.selectedContextBlockIds.includes(blockId)
        ? this.selectedContextBlockIds.filter(item => item !== blockId)
        : [...this.selectedContextBlockIds, blockId]
    },
    clippedContextText (text) {
      const value = String(text || '')
      if (value.length <= contextMaxLength) return value

      return `${value.slice(0, contextMaxLength)}\n\n[Contexto DataJud truncado para geração. Consulte o JSON normalizado na tela de Metadados Judiciais CNJ para o retorno completo.]`
    },
    formatProcessNumber () {
      const digits = this.onlyDigits(this.processNumber).slice(0, 20)
      let value = digits

      if (digits.length > 7) value = `${digits.slice(0, 7)}-${digits.slice(7)}`
      if (digits.length > 9) value = `${digits.slice(0, 7)}-${digits.slice(7, 9)}.${digits.slice(9)}`
      if (digits.length > 13) value = `${digits.slice(0, 7)}-${digits.slice(7, 9)}.${digits.slice(9, 13)}.${digits.slice(13)}`
      if (digits.length > 14) value = `${digits.slice(0, 7)}-${digits.slice(7, 9)}.${digits.slice(9, 13)}.${digits.slice(13, 14)}.${digits.slice(14)}`
      if (digits.length > 16) value = `${digits.slice(0, 7)}-${digits.slice(7, 9)}.${digits.slice(9, 13)}.${digits.slice(13, 14)}.${digits.slice(14, 16)}.${digits.slice(16)}`

      this.processNumber = value
    },
    onlyDigits (value) {
      return String(value || '').replace(/\D/g, '')
    },
    normalizeClientResult (data) {
      const processLimit = 200
      const processes = Array.isArray(data.processes)
        ? data.processes.map(process => {
            const movements = Array.isArray(process.movements) ? process.movements : []
            const total = Number(process.movements_total || movements.length || 0)
            const truncated = Boolean(process.movements_truncated) || movements.length > processLimit

            return {
              ...process,
              parties: Array.isArray(process.parties) ? process.parties : [],
              movements_total: total,
              movements_truncated: truncated,
              movements: movements.slice(0, processLimit)
            }
          })
        : []

      return {
        ...data,
        processes
      }
    },
    identificationItems (process) {
      return [
        { label: 'Número', value: process.formatted_process_number || '-' },
        { label: 'Tribunal', value: process.tribunal || '-' },
        { label: 'Classe', value: this.namedCodeLabel(process.class) },
        { label: 'Sistema', value: this.namedCodeLabel(process.system) },
        { label: 'Formato', value: this.namedCodeLabel(process.format) },
        { label: 'Grau', value: process.degree || '-' },
        { label: 'Nível de Sigilo', value: process.secrecy_level === undefined || process.secrecy_level === null ? '-' : process.secrecy_level },
        { label: 'Órgão Julgador', value: process.judging_body && process.judging_body.name ? process.judging_body.name : '-' },
        { label: 'Data de Ajuizamento', value: this.formatDate(process.filing_date) },
        { label: 'Última atualização', value: this.formatDateTime(process.last_update) }
      ]
    },
    technicalItems (process) {
      const info = process.technical_info || {}
      return [
        { label: 'ID DataJud', value: process.id || info.source_id || '-' },
        { label: 'Índice', value: process.raw_index || info.index || '-' },
        { label: 'Score', value: process.score === undefined || process.score === null ? '-' : process.score },
        { label: 'Código órgão', value: process.judging_body && process.judging_body.code ? process.judging_body.code : '-' },
        { label: 'Município IBGE', value: process.judging_body && process.judging_body.ibge_city_code ? process.judging_body.ibge_city_code : '-' },
        { label: 'Limite movimentos', value: info.movement_limit || '-' },
        { label: 'Total movimentos', value: process.movements_total || 0 },
        { label: 'Movimentos truncados', value: process.movements_truncated ? 'Sim' : 'Não' }
      ]
    },
    namedCodeLabel (value) {
      if (!value) return '-'
      if (value.name && value.code) return `${value.name} (${value.code})`
      return value.name || value.code || '-'
    },
    partyDetail (party) {
      const parts = [
        party.pole ? `Polo ${party.pole}` : null,
        party.role || null,
        party.type || null
      ].filter(Boolean)

      return parts.length ? parts.join(' · ') : 'Sem classificação informada'
    },
    visibleMovements (process) {
      const key = process.id || process.process_number
      return this.expandedMovementKeys.includes(key)
        ? process.movements
        : process.movements.slice(0, this.movementLimit)
    },
    movementCountLabel (process) {
      const total = Number(process.movements_total || process.movements.length || 0)
      if (process.movements_truncated) {
        return `${process.movements.length} de ${total} registro(s)`
      }

      return `${total} registro(s)`
    },
    toggleMovements (key) {
      if (this.expandedMovementKeys.includes(key)) {
        this.expandedMovementKeys = this.expandedMovementKeys.filter(item => item !== key)
      } else {
        this.expandedMovementKeys = [...this.expandedMovementKeys, key]
      }
    },
    formatDate (value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleDateString('pt-BR')
    },
    formatDateTime (value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString('pt-BR')
    }
  }
}
</script>
